import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useContactTitles } from '@/hooks/useContactTitles';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useCustomerContacts } from '@/hooks/useCustomerContacts';
import { validateContactForm } from '@/lib/contact-form-validation';
import { CustomerSearchCombobox } from './customer-search-combobox';

const PREFERRED_CONTACT_METHOD_OPTIONS = ['Email', 'Phone', 'Customer Portal'];

interface AddContactDialogProps {
  customerId?: string;
  defaultCompany?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactCreated?: (contactId: string) => void;
}

export function AddContactDialog({
  customerId,
  defaultCompany,
  open,
  onOpenChange,
  onContactCreated,
}: AddContactDialogProps) {
  const showCustomerPicker = customerId === undefined;
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const effectiveCustomerId = customerId ?? selectedCustomer?.id;
  const { createContact } = useCustomerContacts(effectiveCustomerId ?? '');
  const { data: titleOptions = [] } = useContactTitles();

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    surname: '',
    email: '',
    phone: '',
    mobile: '',
    preferredContactMethod: '',
    jobTitle: '',
    company: defaultCompany ?? '',
    notes: '',
    isPrimary: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData((prev) => ({ ...prev, company: defaultCompany ?? '' }));
    }
  }, [open, defaultCompany]);

  useEffect(() => {
    if (!defaultCompany && selectedCustomer) {
      setFormData((prev) => ({ ...prev, company: selectedCustomer.name }));
    }
  }, [defaultCompany, selectedCustomer]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setFormData({
      title: '',
      firstName: '',
      surname: '',
      email: '',
      phone: '',
      mobile: '',
      preferredContactMethod: '',
      jobTitle: '',
      company: defaultCompany ?? '',
      notes: '',
      isPrimary: false,
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (isSaving || !effectiveCustomerId) return;
    const validationError = validateContactForm(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const result = await createContact({
        title: formData.title || undefined,
        firstName: formData.firstName || undefined,
        surname: formData.surname || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        mobile: formData.mobile || undefined,
        preferredContactMethod: formData.preferredContactMethod || undefined,
        jobTitle: formData.jobTitle,
        company: formData.company,
        notes: formData.notes,
        isPrimary: formData.isPrimary,
      });

      if (!result?.id) {
        toast.error('Contact creation failed');
        return;
      }

      toast.success('Contact added successfully');
      resetForm();
      onOpenChange(false);
      onContactCreated?.(result.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create contact');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>Add a new contact. Complete at least one field.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {showCustomerPicker && (
            <div className="space-y-2">
              <Label htmlFor="add-contact-customer">Customer</Label>
              <CustomerSearchCombobox
                selectedCustomer={selectedCustomer}
                onSelectCustomer={setSelectedCustomer}
                placeholder="Search for a customer..."
              />
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-title">Title</Label>
              <Select value={formData.title} onValueChange={(value) => setFormData({ ...formData, title: value })}>
                <SelectTrigger id="add-contact-title">
                  <SelectValue placeholder="Select title" />
                </SelectTrigger>
                <SelectContent>
                  {titleOptions.map((title) => (
                    <SelectItem key={title} value={title}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-firstName">First Name</Label>
              <Input
                id="add-contact-firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="John"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-surname">Surname</Label>
              <Input
                id="add-contact-surname"
                value={formData.surname}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                placeholder="Smith"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-email">Email</Label>
              <Input
                id="add-contact-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.smith@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-phone">Phone</Label>
              <Input
                id="add-contact-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+44 20 7946 0301"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-mobile">Mobile</Label>
              <Input
                id="add-contact-mobile"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+44 20 7946 0399"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-preferredMethod">Preferred Contact Method</Label>
              <Select
                value={formData.preferredContactMethod}
                onValueChange={(value) => setFormData({ ...formData, preferredContactMethod: value })}
              >
                <SelectTrigger id="add-contact-preferredMethod">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PREFERRED_CONTACT_METHOD_OPTIONS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-jobTitle">Job Title</Label>
            <Input
              id="add-contact-jobTitle"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              placeholder="Account Manager"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-company">Company</Label>
            <Input
              id="add-contact-company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Company Name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-notes">Notes</Label>
            <Textarea
              id="add-contact-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes about this contact..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="add-contact-primary"
              checked={formData.isPrimary}
              onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="add-contact-primary" className="cursor-pointer">
              Set as primary contact
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !effectiveCustomerId}>
            {isSaving ? 'Adding...' : 'Add Contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
